import React, { memo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position } from 'reactflow';
import { downloadFile} from './utils/downloadUtils';
import { getWebSocketUrl } from './utils/websocketUtils';
import { useGlobalConfig } from '../../contexts/GlobalConfigContext';
import RevenueImpactModal from '../RevenueImpactModal';

const DispatcherNode = ({ id, data, isConnectable }) => {
  const { globalConfig, updateGlobalConfig, wcStartDates,
          wcOptimizeEnabled, isWcOptimizeEnabled } = useGlobalConfig();
  
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(data.lastUpdate || Date.now());
  const [isFetching, setIsFetching] = useState(false);
  const [fetchStatus, setFetchStatus] = useState('');
  
  const [isOptimizingAll, _setIsOptimizingAll] = useState(false);
  // Wrap setter to broadcast state changes to MachineNodes
  const setIsOptimizingAll = (value) => {
    _setIsOptimizingAll(value);
    window.dispatchEvent(new CustomEvent('optimizeAllStateChange', { detail: { isOptimizingAll: value, workCenters: optimizingWorkCentersRef.current } }));
  };
  const [optimizationProgress, setOptimizationProgress] = useState({ current: 0, total: 0, currentWC: '' });
  const [hasOptimized, setHasOptimized] = useState(false);
  const [multiWcProgress, setMultiWcProgress] = useState(0);
  const [multiWcStartTime, setMultiWcStartTime] = useState(null);
  const [multiWcBatchNumber, setMultiWcBatchNumber] = useState(0);
  const [multiWcBatchTimeLimit, setMultiWcBatchTimeLimit] = useState(180);
  const [lastOptimizationResults, setLastOptimizationResults] = useState(null);
  const [expectedMachinesCount, setExpectedMachinesCount] = useState(0);
  const [optimizationError, setOptimizationError] = useState(null);
  const [multiWcPostStatus, setMultiWcPostStatus] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false); // e.g. 'Building schedule…'
  const [optimizingWorkCenters, setOptimizingWorkCenters] = useState([]);
  const optimizingWorkCentersRef = React.useRef([]);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const [showResultsBrowser, setShowResultsBrowser] = useState(false);
  const [availableResults, setAvailableResults] = useState([]);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  
  const [showGlobalSettings, setShowGlobalSettings] = useState(false);
  const [showRevenueImpact, setShowRevenueImpact] = useState(false);
  const [isDumpingLogs, setIsDumpingLogs] = useState(false);

  // GA Excel result (the GA runs single-WC on the ROT panel, but its Excel covers
  // all jobs/WCs — so MachineNode announces it here for the main panel to show).
  const [gaExcel, setGaExcel] = useState(null); // { name, url, wc, jobs }
  useEffect(() => {
    const onGaResult = (e) => {
      const d = e.detail;
      if (d && d.excelName && d.excelUrl) {
        setGaExcel({ name: d.excelName, url: d.excelUrl, wc: d.wc, jobs: d.jobs });
      }
    };
    window.addEventListener('gaResultReady', onGaResult);
    return () => window.removeEventListener('gaResultReady', onGaResult);
  }, []);

  const [selectedAlgorithm, setSelectedAlgorithm] = useState('ga');

  const algorithmOptions = [
    { value: 'ga', label: 'Genetic Algorithm', disabled: false },
    { value: 'cp-sat', label: 'CP-SAT (retired)', disabled: true },
    { value: 'simulated-annealing', label: 'Simulated Annealing (Coming Soon)', disabled: true },
    { value: 'tabu-search', label: 'Tabu Search (Coming Soon)', disabled: true }
  ];
  
  const [editableData, setEditableData] = useState({
    label: data.label || 'Job Dispatcher',
    updateInterval: data.updateInterval || 3600000,
    totalJobs: data.totalJobs || 0,
    optimizedJobs: data.optimizedJobs || 0,
    excelPath: ''
  });

  useEffect(() => {
    const savedFileName = localStorage.getItem('dispatcher-last-file-name');
    if (savedFileName) {
      setEditableData(prev => ({ ...prev, excelPath: savedFileName }));
    }
    
    const savedAlgorithm = localStorage.getItem('dispatcher-selected-algorithm');
    if (savedAlgorithm && savedAlgorithm !== 'cp-sat') {
      setSelectedAlgorithm(savedAlgorithm);
    }
  }, []);

  // Fetch all machine optimization states on mount
  useEffect(() => {
    const fetchAllOptimizationStates = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/api/optimization/status`);
        
        if (response.ok) {
          const result = await response.json();
          
          if (result.success && result.optimizations) {
            const states = Object.values(result.optimizations);
            const activeOptimizations = states.filter(s => s.isOptimizing);
            
            if (activeOptimizations.length > 0) {
              console.log(`🔄 Restoring ${activeOptimizations.length} active optimizations`);
              
              // Restore algorithm from Redis if available
              const algorithmFromRedis = activeOptimizations[0]?.algorithm;
              if (algorithmFromRedis && algorithmFromRedis !== 'cp-sat') {
                console.log(`🎯 Restoring algorithm from Redis: ${algorithmFromRedis}`);
                setSelectedAlgorithm(algorithmFromRedis);
                localStorage.setItem('dispatcher-selected-algorithm', algorithmFromRedis);
              }
              
              setIsOptimizingAll(true);
              setOptimizationProgress({
                current: activeOptimizations.length,
                total: activeOptimizations.length,
                currentWC: activeOptimizations.map(s => s.machineName).join(', ')
              });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching optimization states:', error);
      }
    };
    
    fetchAllOptimizationStates();
  }, []);

  // Restore previous multi-WC optimization results on mount
  useEffect(() => {
    const fetchLatestMultiWcResults = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/api/multi-wc/latest-results`);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.hasResults && data.results) {
            console.log(`🔄 [Page Load] Restoring multi-WC results:`, data.results);
            
            setLastOptimizationResults({
              jobsOptimized: data.results.totalJobsOptimized || 0,
              totalBatches: data.results.totalBatches || 0,
              csvFiles: data.results.csvFiles || {},
              excelFile: data.results.excelFile || null,
              jobsByWC: data.results.jobsByWC || {},
              shiftsPerWC: {},
              completionTimePerWC: {},
              timestamp: data.results.timestamp
            });
            setHasOptimized(true);
            console.log(`✅ [Page Load] Restored multi-WC results: ${data.results.totalJobsOptimized} jobs`);
          }
        }
      } catch (error) {
        console.error('❌ [Page Load] Failed to restore multi-WC results:', error);
      }
    };
    
    fetchLatestMultiWcResults();
  }, []);

  // Clear old optimization results when new data is imported
  useEffect(() => {
    const handleImportClear = () => {
      console.log('🧹 New data imported — clearing old optimization results');
      setHasOptimized(false);
      setLastOptimizationResults(null);
      setMultiWcProgress(0);
      setMultiWcStartTime(null);
      setMultiWcBatchNumber(0);
    };
    window.addEventListener('excel-import-completed', handleImportClear);
    return () => window.removeEventListener('excel-import-completed', handleImportClear);
  }, []);

  // Listen for WebSocket optimization events
  useEffect(() => {
    const handleOptimizationEvent = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'optimization_started') {
          console.log(`🚀 Global optimization started`);
          setIsOptimizingAll(true);
        } else if (message.type === 'optimization_completed') {
          console.log(`✅ Machine optimization completed: ${message.data.machineName}`);
          
          // Update progress counter
          setOptimizationProgress(prev => {
            const newCurrent = prev.current + 1;
            console.log(`📊 Progress: ${newCurrent}/${prev.total} machines completed`);
            
            // Update lastOptimizationResults incrementally
            setLastOptimizationResults(prevResults => {
              const currentResults = prevResults || {
                machinesOptimized: 0,
                totalOptimizedJobs: 0,
                totalShiftsUsed: 0,
                totalSolveTime: 0
              };
              
              return {
                machinesOptimized: newCurrent,
                totalOptimizedJobs: currentResults.totalOptimizedJobs + (message.data.totalJobsOptimized || 0),
                totalShiftsUsed: currentResults.totalShiftsUsed + (message.data.totalShifts || 0),
                totalSolveTime: currentResults.totalSolveTime + (message.data.totalTime || 0),
                timestamp: new Date().toISOString()
              };
            });
            
            return {
              ...prev,
              current: newCurrent
            };
          });
          
          // Check if all expected machines are done
          setOptimizationProgress(prev => {
            if (prev.current >= prev.total && prev.total > 0) {
              console.log(`🎉 All ${prev.total} machines complete!`);
              setIsOptimizingAll(false);
              setHasOptimized(true);
              setTimeout(() => {
                setOptimizationProgress({ current: 0, total: 0, currentWC: '' });
              }, 100);
            }
            return prev;
          });
        }
        
        // ========== Multi-WC WebSocket Events ==========
        if (message.type === 'multi_wc_progress') {
          console.log(`📦 Multi-WC batch ${message.data.batchNumber} starting (${message.data.totalJobsProcessed} jobs so far)`);
          // New batch starting — reset progress bar and update batch number
          if (message.data.workCenters) {
            setOptimizingWorkCenters(message.data.workCenters);
            optimizingWorkCentersRef.current = message.data.workCenters;
          }
          // Show progress bar for all optimization runs (single-WC and multi-WC alike)
          setIsOptimizingAll(true);
          setMultiWcBatchNumber(message.data.batchNumber);
          setMultiWcBatchTimeLimit(message.data.timeLimit || 180);
          setMultiWcProgress(0);
          setMultiWcStartTime(Date.now());
          setMultiWcPostStatus(null); // Clear any post-processing status from previous batch
          setOptimizationProgress(prev => ({
            ...prev,
            currentWC: `Batch ${message.data.batchNumber}`
          }));
        } else if (message.type === 'multi_wc_batch_completed') {
          console.log(`✅ Multi-WC batch ${message.data.batchNumber} done: ${message.data.batchJobsCount} jobs (total: ${message.data.totalJobsOptimized})`);
          // Fill progress bar to 100% briefly before next batch resets it
          setMultiWcProgress(100);
          setOptimizationProgress(prev => ({
            ...prev,
            current: message.data.batchNumber,
            total: message.data.batchNumber + 1,
            currentWC: `Batch ${message.data.batchNumber} ✅ (${message.data.totalJobsOptimized} jobs total)`
          }));
        } else if (message.type === 'multi_wc_status') {
          // Python PROGRESS: status lines relayed by backend
          const statusMessages = {
            'schedule_created': '📋 Building schedule…',
            'validating': '🔍 Validating solution…',
            'cost_report': '💰 Calculating costs…',
            'writing_results': '💾 Writing result files…',
            'solver_rescue_from_checkpoint': '🚑 Solver crashed repeatedly — restoring last good solution from checkpoint…'
          };
          let statusText = statusMessages[message.data.status];
          if (!statusText) {
            // solver_retry_<N>_of_<M>: backend emits this when the CP-SAT
            // solver crashes and it auto-restarts from the last checkpoint.
            const retryMatch = /^solver_retry_(\d+)_of_(\d+)$/.exec(message.data.status || '');
            if (retryMatch) {
              statusText = `♻️ Solver crashed — recovering from last solution (attempt ${retryMatch[1]}/${retryMatch[2]})…`;
            } else {
              statusText = `Processing: ${message.data.status}`;
            }
          }
          console.log(`📊 Multi-WC status: ${message.data.status} → ${statusText}`);
          setMultiWcPostStatus(statusText);
        } else if (message.type === 'multi_wc_finalizing') {
          console.log(`💾 Multi-WC finalizing: saving results...`);
          // Jump progress to 99% and show "Saving results…" while CSV/Excel are being written
          setMultiWcProgress(99);
          setMultiWcPostStatus('💾 Saving results…');
          setOptimizationProgress(prev => ({
            ...prev,
            currentWC: `Saving results… (${message.data.totalJobsOptimized} jobs)`
          }));
        } else if (message.type === 'multi_wc_completed') {
          console.log(`🏁 Multi-WC optimization complete:`, message.data);
          setIsOptimizingAll(false);
          setHasOptimized(true);
          setMultiWcPostStatus(null);
          setOptimizationError(null);
          setLastOptimizationResults({
            jobsOptimized: message.data.totalJobsOptimized || 0,
            totalBatches: message.data.totalBatches || 0,
            csvFiles: message.data.csvFiles || {},
            excelFile: message.data.excelFile || null,
            jobsByWC: message.data.jobsByWC || {},
            shiftsPerWC: message.data.shiftsPerWC || {},
            completionTimePerWC: message.data.completionTimePerWC || {},
            // GA: jobs it could give no legal slot (not in any schedule) and hard-rule
            // violations it reported. Both are shown as warnings in the results panel.
            parkedJobs: message.data.parkedJobs || [],
            validationViolations: message.data.validationViolations || []
          });
          setOptimizationProgress({ current: 0, total: 0, currentWC: '' });
          
          // Broadcast per-WC results to MachineNodes
          window.dispatchEvent(new CustomEvent('multiWcResultsReady', {
            detail: {
              csvFiles: message.data.csvFiles || {},
              jobsByWC: message.data.jobsByWC || {},
              shiftsPerWC: message.data.shiftsPerWC || {},
              completionTimePerWC: message.data.completionTimePerWC || {}
            }
          }));
          
          // Trigger data refresh
          if (data.onOptimizeComplete) {
            data.onOptimizeComplete();
          }
        } else if (message.type === 'multi_wc_cancelled') {
          console.log(`🛑 Multi-WC optimization stopped by user:`, message.data);
          setIsOptimizingAll(false);
          setHasOptimized(true);
          setMultiWcPostStatus(null);
          setOptimizationError(null);
          setLastOptimizationResults({
            jobsOptimized: message.data.totalJobsOptimized || 0,
            totalBatches: message.data.totalBatches || 0,
            csvFiles: message.data.csvFiles || {},
            excelFile: message.data.excelFile || null,
            jobsByWC: message.data.jobsByWC || {},
            shiftsPerWC: message.data.shiftsPerWC || {},
            completionTimePerWC: message.data.completionTimePerWC || {},
            wasCancelled: true
          });
          setOptimizationProgress({ current: 0, total: 0, currentWC: '' });

          window.dispatchEvent(new CustomEvent('multiWcResultsReady', {
            detail: {
              csvFiles: message.data.csvFiles || {},
              jobsByWC: message.data.jobsByWC || {},
              shiftsPerWC: message.data.shiftsPerWC || {},
              completionTimePerWC: message.data.completionTimePerWC || {}
            }
          }));

          if (data.onOptimizeComplete) {
            data.onOptimizeComplete();
          }
        } else if (message.type === 'multi_wc_error') {
          console.error(`❌ Multi-WC error:`, message.data.error);
          setIsOptimizingAll(false);
          setMultiWcPostStatus(null);
          setMultiWcProgress(0);
          setMultiWcStartTime(null);
          setMultiWcBatchNumber(0);
          setOptimizationProgress({ current: 0, total: 0, currentWC: '' });
          setOptimizationError(message.data.error || 'Optimization failed unexpectedly');
          
          // Notify MachineNodes about the error so they can show it
          window.dispatchEvent(new CustomEvent('multiWcError', {
            detail: { error: message.data.error || 'Optimization failed unexpectedly', workCenter: message.data.workCenter }
          }));
        }
      } catch (error) {
        // Ignore non-JSON messages
      }
    };
    
    // Connect to WebSocket using the utility function with reconnection
    let ws = null;
    let wsConnected = false;
    let pollingInterval = null;
    let reconnectTimeout = null;
    
    // HTTP Polling fallback for multi-WC completion detection
    const pollMultiWcStatus = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const statusResponse = await fetch(`${apiBaseUrl}/api/multi-wc/status`);
        
        if (statusResponse.ok) {
          const status = await statusResponse.json();
          
          if (!status.isOptimizing && isOptimizingAll) {
            // Optimization finished while we were polling — fetch results
            console.log(`✅ [Polling] Multi-WC optimization completed detected`);
            
            const resultsResponse = await fetch(`${apiBaseUrl}/api/multi-wc/latest-results`);
            if (resultsResponse.ok) {
              const data = await resultsResponse.json();
              if (data.hasResults && data.results) {
                setIsOptimizingAll(false);
                setHasOptimized(true);
                setLastOptimizationResults({
                  jobsOptimized: data.results.totalJobsOptimized || 0,
                  totalBatches: data.results.totalBatches || 0,
                  csvFiles: data.results.csvFiles || {},
                  excelFile: data.results.excelFile || null,
                  jobsByWC: data.results.jobsByWC || {},
                  shiftsPerWC: {},
                  completionTimePerWC: {},
                  timestamp: data.results.timestamp
                });
                setOptimizationProgress({ current: 0, total: 0, currentWC: '' });
                
                // Broadcast per-WC results to MachineNodes
                window.dispatchEvent(new CustomEvent('multiWcResultsReady', {
                  detail: {
                    csvFiles: data.results.csvFiles || {},
                    jobsByWC: data.results.jobsByWC || {},
                    shiftsPerWC: {},
                    completionTimePerWC: {}
                  }
                }));
                
                console.log(`✅ [Polling] Restored results: ${data.results.totalJobsOptimized} jobs`);
              }
            }
          }
        }
      } catch (error) {
        console.error('❌ [Polling] Failed to poll multi-WC status:', error);
      }
    };
    
    const connectWebSocket = () => {
      try {
        const wsUrl = getWebSocketUrl();
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('✅ [Dispatcher] WebSocket connected');
          wsConnected = true;
          
          // Stop polling if it was running
          if (pollingInterval) {
            clearInterval(pollingInterval);
            pollingInterval = null;
            console.log('⏹️ [Dispatcher] Stopped HTTP polling (WebSocket connected)');
          }
        };
        
        ws.onerror = (error) => {
          console.warn('⚠️ [Dispatcher] WebSocket error:', error);
          wsConnected = false;
        };
        
        ws.onclose = () => {
          console.log('🔌 [Dispatcher] WebSocket disconnected');
          wsConnected = false;
          
          // Start HTTP polling as fallback
          if (!pollingInterval) {
            console.log('🔄 [Dispatcher] Starting HTTP polling fallback (every 5 seconds)');
            pollingInterval = setInterval(pollMultiWcStatus, 5000);
            pollMultiWcStatus(); // Immediate poll
          }
          
          // Try to reconnect after 5 seconds
          if (!reconnectTimeout) {
            reconnectTimeout = setTimeout(() => {
              console.log('🔄 [Dispatcher] Attempting to reconnect WebSocket...');
              reconnectTimeout = null;
              connectWebSocket();
            }, 5000);
          }
        };
        
        ws.addEventListener('message', handleOptimizationEvent);
      } catch (error) {
        console.error('❌ [Dispatcher] Failed to create WebSocket:', error);
        wsConnected = false;
        
        // Start polling immediately if WebSocket fails
        if (!pollingInterval) {
          console.log('🔄 [Dispatcher] Starting HTTP polling fallback (WebSocket failed)');
          pollingInterval = setInterval(pollMultiWcStatus, 5000);
          pollMultiWcStatus();
        }
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (ws) {
        ws.removeEventListener('message', handleOptimizationEvent);
        ws.close();
      }
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setEditableData(prev => ({ ...prev, excelPath: file.name }));
      localStorage.setItem('dispatcher-last-file-name', file.name);
      console.log('📁 File selected for upload:', file.name, `(${(file.size / 1024 / 1024).toFixed(2)} MB)`);
    }
  };

  useEffect(() => {
    const fetchJobStats = async () => {
      try {
        const response = await fetch('/api/jobs/stats');
        if (response.ok) {
          const stats = await response.json();
          setEditableData(prev => ({
            ...prev,
            totalJobs: stats.totalJobs || 0,
            optimizedJobs: stats.optimizedJobs || 0
          }));
        }
      } catch (error) {
        console.error('Failed to fetch job stats:', error);
      }
    };

    fetchJobStats();
    const interval = setInterval(fetchJobStats, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMachineUpdate = (event) => {
      console.log('Machine optimization update received:', event.detail);
      fetch('/api/jobs/stats')
        .then(res => res.json())
        .then(stats => {
          setEditableData(prev => ({
            ...prev,
            totalJobs: stats.totalJobs || 0,
            optimizedJobs: stats.optimizedJobs || 0
          }));
        })
        .catch(err => console.error('Failed to fetch job stats:', err));
    };

    window.addEventListener('machine-optimization-complete', handleMachineUpdate);
    return () => window.removeEventListener('machine-optimization-complete', handleMachineUpdate);
  }, []);

  const handleFieldChange = (field, value) => {
    const newValue = field === 'label' ? value : Number(value);
    setEditableData(prev => ({ ...prev, [field]: newValue }));
  };

  const handleAlgorithmChange = (algorithm) => {
    setSelectedAlgorithm(algorithm);
    localStorage.setItem('dispatcher-selected-algorithm', algorithm);
    console.log('📊 Algorithm selected:', algorithm);
  };

  const handleFetchNow = async () => {
    if (!selectedFile) {
      setFetchStatus('Please select an Excel file');
      setTimeout(() => setFetchStatus(''), 3000);
      return;
    }

    setIsFetching(true);
    setFetchStatus('Uploading and processing Excel file...');
    setIsUpdating(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch('/api/import/excel-direct', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || `Import failed: ${response.statusText}`);
      }

      const result = await response.json();
      setLastUpdate(Date.now());
      
      if (result.summary) {
        setEditableData(prev => ({
          ...prev,
          totalJobs: result.summary.jobsCreated || 0
        }));
      }
      
      setFetchStatus(`Success! Imported ${result.summary?.jobsCreated || 0} jobs, ${result.summary?.machinesCreated || 0} machines`);
      
      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('excel-import-completed', { detail: result }));
        window.dispatchEvent(new CustomEvent('excel-path-import-completed', { detail: result }));
      }

      setTimeout(() => setFetchStatus(''), 5000);

    } catch (error) {
      console.error('Upload error:', error);
      setFetchStatus(`Error: ${error.message}`);
      setTimeout(() => setFetchStatus(''), 5000);
    } finally {
      setIsFetching(false);
      setTimeout(() => setIsUpdating(false), 1500);
    }
  };

  // Timer-based progress bar for Multi-WC optimization (resets per batch)
  // Add 5% + 10s buffer to account for Python startup, model building, and
  // post-processing overhead so the bar doesn't cap out long before the batch
  // actually finishes.  Cap at 98% — post-processing status pushes to 99%.
  useEffect(() => {
    if (isOptimizingAll && multiWcStartTime && multiWcProgress < 100) {
      const interval = setInterval(() => {
        // Don't override post-processing status (99%) with timer-based progress
        if (multiWcPostStatus) return;
        const elapsed = Date.now() - multiWcStartTime;
        const timeLimitMs = multiWcBatchTimeLimit * 1000;
        const adjustedTimeLimitMs = timeLimitMs * 1.05 + 10000;
        const progress = Math.min((elapsed / adjustedTimeLimitMs) * 100, 98);
        setMultiWcProgress(progress);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [isOptimizingAll, multiWcStartTime, multiWcBatchTimeLimit, multiWcProgress, multiWcPostStatus]);

  // Completion flash for Multi-WC progress bar (only when all batches done)
  useEffect(() => {
    if (!isOptimizingAll && multiWcProgress > 0) {
      setMultiWcProgress(100);
      setMultiWcPostStatus(null);
      const timeout = setTimeout(() => {
        setMultiWcProgress(0);
        setMultiWcStartTime(null);
        setMultiWcBatchNumber(0);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isOptimizingAll, multiWcProgress]);

  // Safety timeout: if stuck in post-processing for >3 minutes, poll backend
  useEffect(() => {
    if (isOptimizingAll && multiWcPostStatus && multiWcProgress >= 99) {
      const safetyTimeout = setTimeout(async () => {
        console.warn(`⚠️ Multi-WC stuck in post-processing for 3 minutes, polling backend...`);
        try {
          const apiBaseUrl = process.env.REACT_APP_API_URL || '';
          const statusResponse = await fetch(`${apiBaseUrl}/api/multi-wc/status`);
          if (statusResponse.ok) {
            const status = await statusResponse.json();
            if (!status.isRunning) {
              console.log(`✅ Backend says multi-WC is done, forcing completion`);
              setIsOptimizingAll(false);
              setMultiWcPostStatus(null);
              setMultiWcProgress(100);
              // Try to get latest results
              const resultsResponse = await fetch(`${apiBaseUrl}/api/multi-wc/latest-results`);
              if (resultsResponse.ok) {
                const results = await resultsResponse.json();
                if (results && results.totalJobsOptimized > 0) {
                  setHasOptimized(true);
                  setLastOptimizationResults(results);
                }
              }
            }
          }
        } catch (err) {
          console.error('Failed to poll multi-WC status:', err);
        }
      }, 180000); // 3 minutes safety timeout
      return () => clearTimeout(safetyTimeout);
    }
  }, [isOptimizingAll, multiWcPostStatus, multiWcProgress]);

  const handleOptimizeMultiWC = async (e) => {
    e.stopPropagation();
    
    try {
      setIsOptimizingAll(true);
      setIsCancelling(false);
      setHasOptimized(false);
      setLastOptimizationResults(null);
      setOptimizationError(null);
      setMultiWcPostStatus(null);
      setMultiWcStartTime(Date.now());
      setMultiWcProgress(0);
      setOptimizingWorkCenters([]);
      optimizingWorkCentersRef.current = [];
      
      // Fetch enabled work centers from config
      const configResponse = await fetch('/api/config/optimization');
      if (!configResponse.ok) {
        alert('Failed to fetch configuration');
        setIsOptimizingAll(false);
        return;
      }
      
      const configData = await configResponse.json();
      const configuredWorkCenters = configData.config?.enabledWorkCenters || [];

      if (configuredWorkCenters.length === 0) {
        alert('No work centers configured in main_config.yaml');
        setIsOptimizingAll(false);
        return;
      }

      // Only the work centers whose checkbox is ticked (all of them unless the
      // user unticked some — see each MachineNode's header checkbox).
      const enabledWorkCenters = configuredWorkCenters.filter(wc => isWcOptimizeEnabled(wc));
      if (enabledWorkCenters.length === 0) {
        alert('No work centers are selected. Tick at least one work center\'s checkbox to optimize it.');
        setIsOptimizingAll(false);
        return;
      }

      if (!globalConfig.capacityLoaded) {
        alert('No capacity file uploaded. Please upload a Capacity Template (.xlsx) with per-shift hours for each work center before running the optimization.');
        setIsOptimizingAll(false);
        return;
      }
      
      console.log(`🏭 Starting Multi-WC JSSP optimization for: ${enabledWorkCenters.join(', ')}`);
      
      setOptimizationProgress({ 
        current: 0, 
        total: 1, 
        currentWC: 'Multi-WC JSSP' 
      });
      
      // Build per-WC start date overrides: only include WCs whose local
      // start date differs from the global value.
      const perWcStartDates = {};
      if (wcStartDates) {
        for (const [wc, dateStr] of Object.entries(wcStartDates)) {
          if (dateStr && dateStr !== globalConfig.scheduleStartDate) {
            perWcStartDates[wc] = dateStr;
          }
        }
      }
      
      // Fire the async request (backend returns immediately, runs optimization in background)
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/optimize-multi-wc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workCenters: enabledWorkCenters,
          batchSize: globalConfig.batchSize || 40,
          timeLimit: (globalConfig.timeLimit || 5) * 60,  // Convert minutes to seconds
          algorithm: selectedAlgorithm,
          runtimeConfig: {
            scheduleStartDate: globalConfig.scheduleStartDate,
            perWcStartDates: Object.keys(perWcStartDates).length > 0 ? perWcStartDates : undefined
          }
        })
      });
      
      const result = await response.json();
      
      if (!result.success) {
        console.error('❌ Multi-WC optimization failed to start:', result.error);
        alert(`Multi-WC optimization failed to start: ${result.error}`);
        setIsOptimizingAll(false);
        setOptimizationProgress({ current: 0, total: 0, currentWC: '' });
        return;
      }
      
      console.log('🚀 Multi-WC optimization started in background, listening for WebSocket updates...');
      // Don't setIsOptimizingAll(false) here — WebSocket handler will do it on completion
      
    } catch (error) {
      console.error('Error starting multi-WC optimization:', error);
      alert('Multi-WC optimization failed to start. Check console for details.');
      setIsOptimizingAll(false);
      setOptimizationProgress({ current: 0, total: 0, currentWC: '' });
    }
  };




  const handleStopOptimization = async (e) => {
    e.stopPropagation();
    try {
      setIsCancelling(true);
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/optimize-multi-wc/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (!result.success) {
        console.warn('Cancel request rejected:', result.reason);
        setIsCancelling(false);
      }
      // Don't reset UI state here — wait for multi_wc_cancelled WebSocket event
    } catch (error) {
      console.error('Error cancelling optimization:', error);
      setIsCancelling(false);
    }
  };




  const fetchAvailableResults = async () => {
    setIsLoadingResults(true);
    try {
      const response = await fetch('/api/results');
      if (response.ok) {
        const data = await response.json();
        setAvailableResults(data.files || []);
        setShowResultsBrowser(true);
      } else {
        console.error('Failed to fetch results:', response.statusText);
        alert('Failed to load results. Make sure the backend endpoint exists.');
      }
    } catch (error) {
      console.error('Failed to fetch results:', error);
      alert('Error loading results: ' + error.message);
    } finally {
      setIsLoadingResults(false);
    }
  };

  const closeResultsBrowser = () => {
    setShowResultsBrowser(false);
  };

  const handleDumpLogs = async () => {
    setIsDumpingLogs(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      // Collect csvFiles as array (may be object keyed by WC)
      let csvFileList = [];
      if (lastOptimizationResults?.csvFiles) {
        const csv = lastOptimizationResults.csvFiles;
        csvFileList = Array.isArray(csv) ? csv : Object.values(csv);
      }
      const response = await fetch(`${apiBaseUrl}/api/debug-bundle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          csvFiles: csvFileList,
          excelFile: lastOptimizationResults?.excelFile || null,
          workCenters: lastOptimizationResults?.workCenters || Object.keys(lastOptimizationResults?.csvFiles || {}),
          runtimeConfig: lastOptimizationResults?.runtimeConfig || {},
          guiStartDate: globalConfig.scheduleStartDate || null,
          guiPerWcStartDates: wcStartDates || {}
        })
      });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debug_bundle_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download debug bundle:', error);
      alert('Failed to download debug bundle: ' + error.message);
    } finally {
      setIsDumpingLogs(false);
    }
  };


  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Check if it's an Excel serial date (number)
    if (typeof dateString === 'number') {
      // Excel dates are days since January 1, 1900
      const excelEpoch = new Date(1900, 0, 1);
      const daysOffset = dateString - 2; // Excel has a leap year bug for 1900
      const date = new Date(excelEpoch.getTime() + daysOffset * 24 * 60 * 60 * 1000);
      return date.toLocaleDateString();
    }
    
    // Otherwise treat as regular date string
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  useEffect(() => {
    console.log(`Setting update interval to ${editableData.updateInterval}ms`);
    
    const interval = setInterval(() => {
      setIsUpdating(true);
      setLastUpdate(Date.now());
      setTimeout(() => setIsUpdating(false), 1500);
    }, editableData.updateInterval);

    return () => clearInterval(interval);
  }, [editableData.updateInterval]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  const getStatusColor = () => {
    if (isUpdating || isFetching) return '#f39c12';
    return data.connected ? '#27ae60' : '#e74c3c';
  };

  const pendingJobs = editableData.totalJobs - editableData.optimizedJobs;

  return (
    <div className="dispatcher-node" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '16px',
      padding: '16px',
      minWidth: '320px',
      color: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
      position: 'relative'
    }}>
      <Handle
        type="source"
        position={Position.Bottom}
        id="jobs-out"
        style={{ background: '#27ae60', width: 12, height: 12, bottom: -6 }}
        isConnectable={isConnectable}
      />

      <Handle
        type="target"
        position={Position.Top}
        id="status-in"
        style={{ background: '#3498db', width: 12, height: 12, top: -6 }}
        isConnectable={isConnectable}
      />

      <div className="dispatcher-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
        paddingBottom: '8px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="dispatcher-icon" style={{ fontSize: '24px' }}>📡</span>
          <input
            className="dispatcher-label"
            value={editableData.label}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            onFocus={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              outline: 'none',
              width: '150px'
            }}
          />
        </div>
        <div className="status-indicator" style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: getStatusColor(),
          boxShadow: `0 0 10px ${getStatusColor()}`,
          animation: (isUpdating || isFetching) ? 'pulse 2s infinite' : 'none'
        }} />
      </div>

      <div className="excel-config" style={{
        marginBottom: '12px',
        padding: '8px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '8px'
      }}>
        <div className="nodrag">
          <label style={{ 
            fontSize: '10px', 
            opacity: '0.8', 
            display: 'block', 
            marginBottom: '4px' 
          }}>
            Excel File:
          </label>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <input
              type="text"
              value={editableData.excelPath}
              readOnly
              placeholder="No file selected..."
              style={{
                flex: 1,
                padding: '4px 6px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                color: 'white',
                fontSize: '11px',
                outline: 'none',
                cursor: 'default'
              }}
            />
            <label style={{
              padding: '4px 8px',
              background: 'rgba(255, 255, 255, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              borderRadius: '4px',
              color: 'white',
              fontSize: '10px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}>
              📁 Browse
              <input
                type="file"
                accept=".xlsx,.xlsm,.xls"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          {selectedFile && (
            <div style={{
              fontSize: '9px',
              marginTop: '4px',
              opacity: '0.7',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span>Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</span>
              <span>✓ Ready to upload</span>
            </div>
          )}
        </div>
      </div>

      <div className="fetch-config" style={{
        marginBottom: '12px',
        padding: '8px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '8px'
      }}>
        <button
          onClick={handleFetchNow}
          disabled={isFetching || !selectedFile}
          style={{
            width: '100%',
            padding: '6px 12px',
            background: (isFetching || !selectedFile) ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.3)',
            border: 'none',
            borderRadius: '6px',
            color: 'white',
            fontSize: '11px',
            fontWeight: '600',
            cursor: (isFetching || !selectedFile) ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: !selectedFile ? 0.5 : 1
          }}
        >
          {isFetching ? '⏳ Uploading...' : '📤 Upload & Import'}
        </button>
        
        {fetchStatus && (
          <div style={{
            fontSize: '10px',
            marginTop: '4px',
            textAlign: 'center',
            color: fetchStatus.includes('Error') ? '#e74c3c' : 
                   fetchStatus.includes('Success') ? '#27ae60' : '#f39c12',
            fontWeight: '500',
            wordBreak: 'break-word'
          }}>
            {fetchStatus}
          </div>
        )}
      </div>

      <div className="dispatcher-updates" style={{ marginBottom: '12px' }}>
        <div className="update-info" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <span className="update-label" style={{ fontSize: '12px', opacity: '0.8' }}>Last Update:</span>
          <span className="update-time" style={{ fontSize: '12px', fontWeight: '500' }}>
            {formatTime(lastUpdate)}
          </span>
        </div>
        
        <div className="update-indicator-persistent" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <div className="update-progress-bar" style={{
            height: '3px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div className="update-progress-fill" style={{
              height: '100%',
              background: (isUpdating || isFetching)
                ? 'linear-gradient(90deg, #a7f3d0, #34d399)' 
                : 'rgba(167, 243, 208, 0.5)',
              animation: (isUpdating || isFetching) ? 'progress-fill 1.5s ease-in-out infinite' : 'none',
              transition: 'all 0.3s ease'
            }}></div>
          </div>
        </div>
      </div>

      <div className="dispatcher-activity" style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginBottom: '12px',
        padding: '10px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '8px'
      }}>
        <div className="total-jobs" style={{
          fontSize: '16px',
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#fff'
        }}>
          {editableData.totalJobs} jobs
        </div>
        
        <div className="job-breakdown" style={{
          display: 'flex',
          justifyContent: 'space-around',
          gap: '12px'
        }}>
          <div className="job-stat" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1
          }}>
            <span style={{ fontSize: '11px', opacity: '0.8', marginBottom: '4px' }}>Optimized</span>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#2ecc71' }}>
              {editableData.optimizedJobs}
            </span>
          </div>
          
          <div style={{
            width: '1px',
            background: 'rgba(255, 255, 255, 0.2)'
          }} />
          
          <div className="job-stat" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1
          }}>
            <span style={{ fontSize: '11px', opacity: '0.8', marginBottom: '4px' }}>Pending</span>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#f39c12' }}>
              {pendingJobs}
            </span>
          </div>
        </div>
      </div>

      <div className="algorithm-section" style={{
        marginBottom: '12px',
        padding: '8px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '8px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '11px', opacity: '0.8', whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            Algorithm:
          </label>
          <select
            value={selectedAlgorithm}
            onChange={(e) => handleAlgorithmChange(e.target.value)}
            className="nodrag"
            style={{
              flex: 1,
              padding: '6px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '4px',
              color: 'white',
              fontSize: '11px',
              fontWeight: '500',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            {algorithmOptions.map(opt => (
              <option
                key={opt.value}
                value={opt.value}
                disabled={opt.disabled}
                style={{
                  background: '#2c3e50',
                  color: opt.disabled ? '#95a5a6' : 'white'
                }}
              >
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* GA result Excel (covers all jobs/WCs) — announced by the ROT panel */}
        {gaExcel && (
          <div className="nodrag" style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
            <span style={{ flex: 1, opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              🧬 GA Excel ({gaExcel.wc}, {gaExcel.jobs} jobs): {gaExcel.name}
            </span>
            <button
              onClick={() => downloadFile(gaExcel.url, gaExcel.name)}
              style={{ padding: '4px 8px', fontSize: '11px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Download
            </button>
          </div>
        )}
      </div>

      {/* Global Settings Section */}
      <div className="global-settings-section" style={{
        marginBottom: '12px',
        padding: '8px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '8px'
      }}>
        <button
          onClick={() => setShowGlobalSettings(!showGlobalSettings)}
          className="nodrag"
          style={{
            width: '100%',
            padding: '6px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            color: 'white',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>⚙️ Global Settings</span>
          <span>{showGlobalSettings ? '▼' : '▶'}</span>
        </button>

        {showGlobalSettings && (
          <div className="nodrag" style={{
            marginTop: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {/* Time Limit */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '10px', opacity: '0.8', minWidth: '100px' }}>Time Limit (min):</label>
              <input
                type="number"
                min="1"
                max="120"
                value={globalConfig.timeLimit}
                onChange={(e) => updateGlobalConfig({ timeLimit: parseFloat(e.target.value) })}
                style={{
                  flex: 1,
                  padding: '4px 6px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '4px',
                  color: 'white',
                  fontSize: '11px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Batch Size */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '10px', opacity: '0.8', minWidth: '100px' }}>Batch Size:</label>
              <input
                type="number"
                min="1"
                max="500"
                value={globalConfig.batchSize}
                onChange={(e) => updateGlobalConfig({ batchSize: parseInt(e.target.value) })}
                style={{
                  flex: 1,
                  padding: '4px 6px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '4px',
                  color: 'white',
                  fontSize: '11px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Capacity File Upload */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '10px', opacity: '0.8', minWidth: '100px' }}>Capacity File:</label>
              <label
                style={{
                  flex: 1,
                  padding: '4px 6px',
                  background: globalConfig.capacityLoaded ? 'rgba(39, 174, 96, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                  border: `1px solid ${globalConfig.capacityLoaded ? 'rgba(39, 174, 96, 0.6)' : 'rgba(255, 255, 255, 0.3)'}`,
                  borderRadius: '4px',
                  color: globalConfig.capacityLoaded ? 'white' : 'rgba(255, 255, 255, 0.5)',
                  fontSize: '10px',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={globalConfig.capacityFileName || 'Click to select a Capacity Template (.xlsx)'}
              >
                {globalConfig.capacityFileName || 'Choose file...'}
                <input
                  type="file"
                  accept=".xlsx,.xlsm,.xls"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
                      const resp = await fetch(`${apiBaseUrl}/api/upload/capacity-file`, {
                        method: 'POST',
                        body: formData
                      });
                      const result = await resp.json();
                      if (result.success) {
                        updateGlobalConfig({ 
                          capacityLoaded: true, 
                          capacityFileName: result.originalName
                        });
                      } else {
                        alert('Failed to upload capacity file: ' + (result.error || 'Unknown error'));
                      }
                    } catch (err) {
                      alert('Error uploading capacity file: ' + err.message);
                    }
                    e.target.value = '';
                  }}
                />
              </label>
            </div>



            {/* Schedule Start Date */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '10px', opacity: '0.8', minWidth: '100px' }}>Start Date:</label>
              <input
                type="text"
                placeholder="MM/DD/YYYY [HH:MM]"
                title="e.g. 03/23/2026 or 03/23/2026 07:00 — hour defaults to 05:00 if omitted"
                value={globalConfig.scheduleStartDate}
                onChange={(e) => {
                  console.log(`📅 DispatcherNode: Updating scheduleStartDate to "${e.target.value}"`);
                  updateGlobalConfig({ scheduleStartDate: e.target.value });
                }}
                style={{
                  flex: 1,
                  padding: '4px 6px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '4px',
                  color: 'white',
                  fontSize: '11px',
                  outline: 'none'
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="optimize-section" style={{ marginBottom: '12px' }}>
        <button 
          className="optimize-multi-wc-btn"
          onClick={handleOptimizeMultiWC}
          disabled={isOptimizingAll || editableData.totalJobs === 0}
          style={{
            width: '100%',
            padding: '12px 16px',
            marginTop: '8px',
            background: (isOptimizingAll || editableData.totalJobs === 0)
              ? '#95a5a6'
              : 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
            color: (isOptimizingAll || editableData.totalJobs === 0) ? '#bdc3c7' : 'white',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: (isOptimizingAll || editableData.totalJobs === 0) ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            opacity: (isOptimizingAll || editableData.totalJobs === 0) ? 0.5 : 1,
            boxShadow: (isOptimizingAll || editableData.totalJobs === 0) 
              ? 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
              : '0 4px 12px rgba(39, 174, 96, 0.4)',
            pointerEvents: (isOptimizingAll || editableData.totalJobs === 0) ? 'none' : 'auto'
          }}
          onMouseEnter={(e) => {
            if (!isOptimizingAll && editableData.totalJobs > 0) {
              e.target.style.background = 'linear-gradient(135deg, #229954 0%, #1e8449 100%)';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(39, 174, 96, 0.6)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isOptimizingAll && editableData.totalJobs > 0) {
              e.target.style.background = 'linear-gradient(135deg, #27ae60 0%, #229954 100%)';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(39, 174, 96, 0.4)';
            }
          }}
        >
          {isOptimizingAll
            ? (optimizingWorkCenters.length === 1
              ? `🔄 Optimizing ${optimizingWorkCenters[0]}...`
              : '🔄 Running Multi-WC JSSP...')
            : editableData.totalJobs === 0
              ? '⏳ Upload Jobs First'
              /* Say which it is: with a work center unticked this no longer runs
                 them all, and the button must not claim otherwise. */
              : (Object.values(wcOptimizeEnabled || {}).some(v => v === false)
                ? '🏭 Optimize Selected Work Centers'
                : '🏭 Optimize All')}
        </button>

        {/* Stop Optimization Button */}
        {isOptimizingAll && (
          <button
            onClick={handleStopOptimization}
            disabled={isCancelling}
            style={{
              width: '100%',
              padding: '8px 16px',
              marginTop: '6px',
              background: isCancelling
                ? '#95a5a6'
                : 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: isCancelling ? 'not-allowed' : 'pointer',
              boxShadow: isCancelling
                ? 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
                : '0 4px 12px rgba(231, 76, 60, 0.4)',
              transition: 'all 0.2s ease',
              opacity: isCancelling ? 0.7 : 1
            }}
          >
            {isCancelling ? '⏳ Stopping...' : '⏹ Stop Optimization'}
          </button>
        )}

        {/* Multi-WC Progress Bar */}
        {/* Gated on multiWcStartTime, not multiWcBatchNumber: the GA path (now the only
            algorithm — CP-SAT was retired from the UI) broadcasts multi_wc_progress with
            no batchNumber, so the old `multiWcBatchNumber > 0` gate hid this bar for every
            run. The handler sets multiWcStartTime whenever a run reports progress. */}
        {isOptimizingAll && multiWcStartTime && (
          <div style={{ marginTop: '8px', padding: '8px 12px', background: 'rgba(39, 174, 96, 0.1)', border: '1px solid rgba(39, 174, 96, 0.3)', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'bold' }}>
                {multiWcPostStatus ? '💾 Finalizing' : '⚙️ Optimizing...'}
              </span>
              <span style={{ fontSize: '11px', color: multiWcPostStatus ? '#f39c12' : '#27ae60', fontWeight: 'bold' }}>{Math.round(multiWcProgress)}%</span>
            </div>
            <div style={{ width: '100%', height: '6px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${multiWcProgress}%`, height: '100%', background: multiWcPostStatus ? 'linear-gradient(90deg, #f39c12, #e67e22)' : 'linear-gradient(90deg, #27ae60, #2ecc71)', borderRadius: '3px', transition: multiWcProgress === 0 ? 'none' : 'width 0.2s ease' }} />
            </div>
            {/* Status text below the progress bar */}
            {multiWcPostStatus && (
              <div style={{ fontSize: '10px', color: '#f39c12', marginTop: '4px', textAlign: 'center', fontStyle: 'italic' }}>
                {multiWcPostStatus}
              </div>
            )}
            {!multiWcPostStatus && multiWcProgress >= 98 && (
              <div style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '4px', textAlign: 'center', fontStyle: 'italic' }}>
                ⏳ Solver finishing up…
              </div>
            )}
          </div>
        )}

        {/* Optimization Error Display */}
        {optimizationError && (
          <div style={{
            marginTop: '8px',
            padding: '8px 12px',
            background: '#fff5f5',
            border: '1px solid #e74c3c',
            borderRadius: '8px',
            fontSize: '11px',
            color: '#c0392b'
          }}>
            ❌ {optimizationError}
            <div 
              style={{ marginTop: '4px', cursor: 'pointer', fontSize: '10px', color: '#c0392b', textDecoration: 'underline', opacity: 0.7 }}
              onClick={() => setOptimizationError(null)}
            >
              Dismiss
            </div>
          </div>
        )}

        {lastOptimizationResults && lastOptimizationResults.csvFiles && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            background: 'rgba(39, 174, 96, 0.1)',
            border: '1px solid rgba(39, 174, 96, 0.3)',
            borderRadius: '8px'
          }}>
            <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 'bold', color: lastOptimizationResults.wasCancelled ? '#f39c12' : '#27ae60' }}>
              {lastOptimizationResults.wasCancelled ? '⏹ Optimization Stopped' : '✅ Multi-WC Optimization Complete'}
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '8px' }}>
              {lastOptimizationResults.jobsOptimized} jobs optimized{lastOptimizationResults.totalBatches > 1 ? ` (${lastOptimizationResults.totalBatches} batches)` : ''}
            </div>
            {Array.isArray(lastOptimizationResults.parkedJobs) && lastOptimizationResults.parkedJobs.length > 0 && (
              <div style={{ fontSize: '11px', color: '#f39c12', marginBottom: '8px' }} title={lastOptimizationResults.parkedJobs.map(p => `${p.job_id} (${p.work_center}): ${p.reason}`).join('\n')}>
                ⚠ {lastOptimizationResults.parkedJobs.length} job(s) PARKED — no legal slot, not scheduled: {lastOptimizationResults.parkedJobs.slice(0, 3).map(p => p.job_id).join(', ')}{lastOptimizationResults.parkedJobs.length > 3 ? ', …' : ''} (Excel rows marked "parked:")
              </div>
            )}
            {Array.isArray(lastOptimizationResults.validationViolations) && lastOptimizationResults.validationViolations.length > 0 && (
              <div style={{ fontSize: '11px', color: '#e74c3c', marginBottom: '8px' }} title={lastOptimizationResults.validationViolations.join('\n')}>
                ❌ {lastOptimizationResults.validationViolations.length} hard-constraint violation(s) reported — do not run this schedule as-is
              </div>
            )}

            {Object.entries(lastOptimizationResults.jobsByWC || {}).filter(([, count]) => count > 0).map(([wc, count]) => (
              <div key={wc} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '4px 8px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
                marginBottom: '2px'
              }}>
                <span style={{ fontSize: '11px', color: 'white' }}>
                  WC {wc}: {count} jobs
                </span>
              </div>
            ))}
            
            {lastOptimizationResults.excelFile && (() => {
              const excelFileName = lastOptimizationResults.excelFile.split('/').pop();
              return (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 8px',
                  background: 'rgba(52, 152, 219, 0.1)',
                  borderRadius: '4px',
                  marginTop: '8px'
                }}>
                  <span style={{ fontSize: '11px', color: 'white', fontWeight: 'bold' }}>
                    📊 Combined Excel
                  </span>
                  <button
                    onClick={() => {
                      downloadFile(`/api/download/${excelFileName}`, excelFileName);
                    }}
                    style={{
                      padding: '4px 8px',
                      background: 'rgba(52, 152, 219, 0.2)',
                      border: '1px solid rgba(52, 152, 219, 0.4)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    📥 XLSX
                  </button>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Revenue Impact Button */}
      {lastOptimizationResults && lastOptimizationResults.csvFiles && (
        <div style={{ marginBottom: '12px' }}>
          <button
            onClick={() => setShowRevenueImpact(true)}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              letterSpacing: '0.5px',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.boxShadow = '0 4px 16px rgba(37, 99, 235, 0.5)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            📊 Revenue Graph
          </button>
        </div>
      )}

      <div className="results-browser-section" style={{ display: 'flex', gap: '6px' }}>
        <button
          onClick={fetchAvailableResults}
          disabled={isLoadingResults}
          style={{
            flex: 1,
            padding: '8px 12px',
            background: isLoadingResults ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.25)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '6px',
            color: 'white',
            fontSize: '11px',
            fontWeight: '600',
            cursor: isLoadingResults ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          {isLoadingResults ? '⏳ Loading...' : '📂 View All Results'}
        </button>
        <button
          onClick={handleDumpLogs}
          disabled={isDumpingLogs}
          title="Download a ZIP with logs, configs, inputs and results for debugging"
          style={{
            flex: 1,
            padding: '8px 12px',
            background: isDumpingLogs
              ? 'rgba(255, 255, 255, 0.2)'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.35) 0%, rgba(234, 179, 8, 0.35) 100%)',
            border: '1px solid rgba(239, 68, 68, 0.4)',
            borderRadius: '6px',
            color: 'white',
            fontSize: '11px',
            fontWeight: '600',
            cursor: isDumpingLogs ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          {isDumpingLogs ? '⏳ Bundling...' : '🐛 Dump Logs'}
        </button>
      </div>

      {/* Revenue Impact Modal */}
      {showRevenueImpact && lastOptimizationResults?.csvFiles && (
        <RevenueImpactModal
          onClose={() => setShowRevenueImpact(false)}
          csvFilenames={lastOptimizationResults.csvFiles}
          capacityData={globalConfig.capacityData || null}
          scheduleStartDate={globalConfig.scheduleStartDate || ''}
        />
      )}

      {showResultsBrowser && createPortal(
        <div 
          className="results-browser-overlay" 
          onClick={closeResultsBrowser}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
        >
          <div 
            className="results-browser-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div className="results-browser-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '2px solid #667eea'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '20px',
                color: '#2c3e50',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                📂 Optimization Results
              </h3>
              <button 
                onClick={closeResultsBrowser}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#95a5a6',
                  padding: 0,
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            <div className="results-browser-body">
              {availableResults.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#95a5a6'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                  <p style={{ fontSize: '16px', margin: 0 }}>No results available yet</p>
                  <p style={{ fontSize: '14px', margin: '8px 0 0 0' }}>
                    Run an optimization to generate result files
                  </p>
                </div>
              ) : (
                <div className="results-list">
                  {availableResults.map((file, index) => {
                    const isCSV = file.name.endsWith('.csv');
                    const isTXT = file.name.endsWith('.txt');
                    const fileIcon = isCSV ? '📊' : isTXT ? '📄' : '📁';
                    
                    return (
                      <div 
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          background: index % 2 === 0 ? '#f8f9fa' : 'white',
                          borderRadius: '6px',
                          marginBottom: '8px',
                          border: '1px solid #e1e8ed',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#e8eef7';
                          e.currentTarget.style.borderColor = '#667eea';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = index % 2 === 0 ? '#f8f9fa' : 'white';
                          e.currentTarget.style.borderColor = '#e1e8ed';
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            marginBottom: '4px'
                          }}>
                            <span style={{ fontSize: '20px' }}>{fileIcon}</span>
                            <span style={{ 
                              fontSize: '13px', 
                              fontWeight: '600',
                              color: '#2c3e50',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {file.name}
                            </span>
                          </div>
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#7f8c8d',
                            marginLeft: '28px'
                          }}>
                            {formatFileSize(file.size)} • {formatDate(file.modified)}
                          </div>
                        </div>
                        <button
                          onClick={() => downloadFile(file.downloadUrl, file.name)}
                          style={{
                            padding: '8px 16px',
                            background: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            marginLeft: '12px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#5a6fd8';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#667eea';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          📥 Download
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{
              marginTop: '20px',
              paddingTop: '16px',
              borderTop: '1px solid #e1e8ed',
              fontSize: '12px',
              color: '#7f8c8d',
              textAlign: 'center'
            }}>
              Total: {availableResults.length} file{availableResults.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default memo(DispatcherNode);